import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useAppContext } from '../../context/AppContext';
import { apiService, Document, DocumentType } from '../../services/apiService';
import { 
  Folder, 
  FileText, 
  FileSpreadsheet, 
  FileImage, 
  ChevronRight, 
  ChevronDown,
  Download,
  Eye,
  X,
  Upload,
  Plus,
  MessageCircle,
  BookOpen,
  FileSearch,
  Loader2,
  AlertCircle
} from 'lucide-react';

interface DocumentCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  processorType: string;
  documents: Document[];
}

const DocumentsTab: React.FC = () => {
  const { state, documents, loading, refetchData } = useAppContext();
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['communications', 'manuals', 'as-builts']));
  const [uploadingCategory, setUploadingCategory] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<Array<any>>([]);
  const [showResults, setShowResults] = useState<boolean>(false);

  // Document categories with upload configuration
  const categories: DocumentCategory[] = [
    {
      id: 'communications',
      name: 'Site Communications',
      description: 'Reports, announcements, and communications',
      icon: <MessageCircle className="w-5 h-5 text-blue-500" />,
      processorType: 'litellm',
      documents: documents.filter(doc => 
        doc.document_type_name?.toLowerCase().includes('communication') || 
        doc.document_type_name?.toLowerCase().includes('report') ||
        doc.tags?.some(tag => tag.tag_category === 'document_category' && tag.tag_value === 'communications')
      )
    },
    {
      id: 'manuals',
      name: 'Manuals',
      description: 'Technical manuals and documentation',
      icon: <BookOpen className="w-5 h-5 text-green-500" />,
      processorType: 'manual', // No processing, just upload
      documents: documents.filter(doc => 
        doc.document_type_name?.toLowerCase().includes('manual') ||
        doc.document_type_name?.toLowerCase().includes('technical') ||
        doc.tags?.some(tag => tag.tag_category === 'document_category' && tag.tag_value === 'manuals')
      )
    },
    {
      id: 'as-builts',
      name: 'As-Builts',
      description: 'As-built drawings and specifications',
      icon: <FileSearch className="w-5 h-5 text-purple-500" />,
      processorType: 'as-built',
      documents: documents.filter(doc => 
        doc.document_type_name?.toLowerCase().includes('as-built') ||
        doc.document_type_name?.toLowerCase().includes('drawing') ||
        doc.document_type_name?.toLowerCase().includes('layout') ||
        doc.tags?.some(tag => tag.tag_category === 'document_category' && tag.tag_value === 'as-builts')
      )
    }
  ];

  useEffect(() => {
    fetchDocumentTypes();
  }, [state.selectedSite]);

  const fetchDocumentTypes = async () => {
    try {
      const types = await apiService.getDocumentTypes();
      setDocumentTypes(types);
    } catch (err) {
      console.error('Error fetching document types:', err);
    }
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getFileIcon = (mimeType: string, fileName: string) => {
    if (mimeType?.includes('pdf') || fileName?.toLowerCase().endsWith('.pdf')) {
      return <FileText className="w-5 h-5 text-accent-red" />;
    } else if (mimeType?.includes('spreadsheet') || fileName?.toLowerCase().match(/\.(xlsx?|csv)$/)) {
      return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    } else if (mimeType?.includes('word') || fileName?.toLowerCase().match(/\.docx?$/)) {
      return <FileText className="w-5 h-5 text-blue-500" />;
    } else if (mimeType?.includes('image') || fileName?.toLowerCase().match(/\.(png|jpg|jpeg|gif)$/)) {
      return <FileImage className="w-5 h-5 text-purple-500" />;
    }
    return <FileText className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleDocumentClick = (doc: Document) => {
    setSelectedDocument(doc);
    setViewerOpen(true);
  };

  const handleFileUpload = async (categoryId: string, files: FileList) => {
    if (!files.length || !state.selectedSite) return;
    
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    setUploadingCategory(categoryId);
    setUploadError(null);
    setUploadResults([]);
    
    try {
      const results = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('site_id', state.selectedSite);
        formData.append('title', file.name.replace(/\.[^/.]+$/, '')); // Remove extension
        formData.append('description', `Uploaded to ${category.name}`);
        formData.append('processor_type', category.processorType);
        formData.append('process_immediately', 'true');

        const result = await apiService.uploadDocument(formData);
        
        // Store results for display
        results.push({
          fileName: file.name,
          category: category.name,
          processorType: category.processorType,
          ...result
        });
      }
      
      setUploadResults(results);
      setShowResults(true);
      
      // Refresh documents after upload
      await refetchData();
      
    } catch (err) {
      setUploadError(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Upload error:', err);
    } finally {
      setUploadingCategory(null);
    }
  };

  const renderCategory = (category: DocumentCategory) => {
    const isExpanded = expandedCategories.has(category.id);
    const isUploading = uploadingCategory === category.id;
    
    return (
      <div key={category.id} className="mb-4">
        <div
          className="flex items-center justify-between px-3 py-2 bg-dark-800 rounded-lg cursor-pointer hover:bg-dark-700 transition-colors"
          onClick={() => toggleCategory(category.id)}
        >
          <div className="flex items-center gap-3">
            <div className="w-4 h-4">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </div>
            {category.icon}
            <div>
              <h3 className="text-lg font-semibold text-gray-200">{category.name}</h3>
              <p className="text-sm text-gray-500">{category.description}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 bg-dark-700 px-2 py-1 rounded">
              {category.documents.length} files
            </span>
            <label className={`flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded cursor-pointer transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Upload
              <input
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.xlsx,.csv,.png,.jpg,.jpeg"
                onChange={(e) => e.target.files && handleFileUpload(category.id, e.target.files)}
                disabled={isUploading}
              />
            </label>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-2 ml-4 space-y-1">
            {category.documents.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                <p>No documents in this category</p>
                <p className="text-sm mt-1">Upload files to get started</p>
              </div>
            ) : (
              category.documents.map(doc => renderDocument(doc))
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDocument = (doc: Document) => {
    return (
      <div
        key={doc.document_id}
        className={`flex items-center gap-3 px-3 py-2 hover:bg-dark-800 rounded cursor-pointer group ${
          selectedDocument?.document_id === doc.document_id ? 'bg-dark-800' : ''
        }`}
        onClick={() => handleDocumentClick(doc)}
      >
        {getFileIcon(doc.mime_type, doc.file_name)}
        
        <div className="flex-1 min-w-0">
          <div className="text-sm text-gray-200 truncate font-medium">{doc.title || doc.file_name}</div>
          {doc.description && (
            <div className="text-xs text-gray-500 truncate mt-1">{doc.description}</div>
          )}
        </div>
        
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>{formatFileSize(doc.file_size_bytes)}</span>
          <span>{format(new Date(doc.created_at), 'MMM d, yyyy')}</span>
          <div className="opacity-0 group-hover:opacity-100 flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(doc.file_path, '_blank');
              }}
              className="p-1 hover:bg-dark-700 rounded"
              title="Download"
            >
              <Download className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDocument(doc);
                setViewerOpen(true);
              }}
              className="p-1 hover:bg-dark-700 rounded"
              title="View Details"
            >
              <Eye className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!state.selectedSite) {
    return (
      <div className="w-full h-full bg-dark-900 flex items-center justify-center">
        <div className="text-center text-gray-500">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <p className="text-lg">No Site Selected</p>
          <p className="text-sm mt-2">Please select a site to view documents</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-dark-900 flex">
      <div className="flex-1 overflow-auto">
        <div className="p-4">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-100 mb-2">Site Documents</h2>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>{documents.length} total documents</span>
              {state.selectedSite && (
                <span className="bg-dark-700 px-2 py-1 rounded text-xs">
                  Site: {state.selectedSite.slice(0, 8)}...
                </span>
              )}
            </div>
          </div>

          {uploadError && (
            <div className="mb-4 p-3 bg-accent-red/20 border border-accent-red rounded-lg flex items-center gap-2 text-accent-red">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{uploadError}</span>
              <button
                onClick={() => setUploadError(null)}
                className="ml-auto text-accent-red hover:text-accent-red/80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {showResults && uploadResults.length > 0 && (
            <div className="mb-4 p-4 bg-green-900/50 border border-green-700 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-green-300">Upload Results</h3>
                <button
                  onClick={() => setShowResults(false)}
                  className="text-green-400 hover:text-green-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {uploadResults.map((result, index) => (
                <div key={index} className="mb-3 p-3 bg-dark-800 rounded border border-dark-600">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-green-400" />
                    <span className="font-medium text-gray-200">{result.fileName}</span>
                    <span className="text-xs bg-dark-700 px-2 py-1 rounded text-gray-400">
                      {result.category}
                    </span>
                  </div>
                  
                  {result.processing_status === 'completed' && result.processorType === 'as-built' && (
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-gray-500">Entities Created:</span>
                          <span className="ml-2 text-green-400 font-medium">{result.entities_created || '0'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Connections:</span>
                          <span className="ml-2 text-green-400 font-medium">{result.connections_created || '0'}</span>
                        </div>
                      </div>
                      
                      {result.site_updated && result.site_metadata && (
                        <div className="mt-3 p-2 bg-blue-900/30 border border-blue-700 rounded">
                          <div className="text-blue-300 font-medium mb-2">Site Updated with Extracted Metadata:</div>
                          <div className="space-y-1 text-xs">
                            {result.site_metadata.project_name && (
                              <div><span className="text-gray-500">Project Name:</span> <span className="text-blue-200">{result.site_metadata.project_name}</span></div>
                            )}
                            {result.site_metadata.project_address && (
                              <div><span className="text-gray-500">Address:</span> <span className="text-blue-200">{result.site_metadata.project_address}</span></div>
                            )}
                            {result.site_metadata.project_capacity_mw && (
                              <div><span className="text-gray-500">Capacity:</span> <span className="text-blue-200">{result.site_metadata.project_capacity_mw} MW</span></div>
                            )}
                            {result.site_metadata.project_lat_lon && (
                              <div><span className="text-gray-500">Location:</span> <span className="text-blue-200">{result.site_metadata.project_lat_lon}</span></div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {result.extraction_summary && (
                        <div className="mt-2 text-xs text-gray-400">
                          Extracted: {result.extraction_summary.nodes_extracted} components, {result.extraction_summary.edges_extracted} connections
                          {result.extraction_summary.project_name && result.extraction_summary.project_name !== 'Unknown' && (
                            <span> from "{result.extraction_summary.project_name}"</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {result.processing_status === 'failed' && (
                    <div className="text-accent-red text-sm">
                      Processing failed: {result.processing_error || 'Unknown error'}
                    </div>
                  )}
                  
                  {result.processing_status === 'manual_upload' && (
                    <div className="text-gray-400 text-sm">
                      Document uploaded successfully (no processing required)
                    </div>
                  )}
                  
                  {result.processing_status === 'completed' && result.processorType === 'litellm' && (
                    <div className="space-y-1 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-gray-500">Events Created:</span>
                          <span className="ml-2 text-green-400 font-medium">{result.events_created || '0'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Concepts:</span>
                          <span className="ml-2 text-green-400 font-medium">{result.concepts_created || '0'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              <span className="ml-3 text-gray-400">Loading...</span>
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map(category => renderCategory(category))}
            </div>
          )}
        </div>
      </div>
      
      {viewerOpen && selectedDocument && (
        <div className="w-96 border-l border-dark-700 bg-dark-800 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-dark-700">
            <h3 className="text-lg font-semibold text-gray-200">Document Details</h3>
            <button
              onClick={() => {
                setViewerOpen(false);
                setSelectedDocument(null);
              }}
              className="p-1 hover:bg-dark-700 rounded"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <div className="flex-1 p-4 overflow-auto">
            <div className="mb-4">
              {getFileIcon(selectedDocument.mime_type, selectedDocument.file_name)}
              <h4 className="text-md font-semibold text-gray-200 mt-2">
                {selectedDocument.title || selectedDocument.file_name}
              </h4>
              <p className="text-sm text-gray-500 mt-1">{selectedDocument.file_name}</p>
            </div>
            
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-gray-500">Size:</span>
                <span className="ml-2 text-gray-300">{formatFileSize(selectedDocument.file_size_bytes)}</span>
              </div>
              <div>
                <span className="text-gray-500">Type:</span>
                <span className="ml-2 text-gray-300">{selectedDocument.mime_type}</span>
              </div>
              <div>
                <span className="text-gray-500">Uploaded:</span>
                <span className="ml-2 text-gray-300">
                  {format(new Date(selectedDocument.created_at), 'MMMM d, yyyy HH:mm')}
                </span>
              </div>
              {selectedDocument.description && (
                <div>
                  <span className="text-gray-500">Description:</span>
                  <p className="text-gray-300 mt-1">{selectedDocument.description}</p>
                </div>
              )}
              {selectedDocument.tags && selectedDocument.tags.length > 0 && (
                <div>
                  <span className="text-gray-500">Tags:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedDocument.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-dark-700 text-xs rounded">
                        {tag.tag_value}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-6 p-4 bg-dark-900 rounded-lg border border-dark-700">
              <p className="text-center text-gray-500 text-sm">
                Document preview not available
              </p>
              <p className="text-center text-gray-600 text-xs mt-1">
                Use the download or open buttons below
              </p>
            </div>
            
            <div className="mt-4 flex gap-2">
              <button 
                onClick={() => window.open(selectedDocument.file_path, '_blank')}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button 
                onClick={() => window.open(selectedDocument.file_path, '_blank')}
                className="flex-1 px-4 py-2 bg-dark-700 text-gray-300 rounded hover:bg-dark-600 flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Open
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;